import { AIR, hash32 } from '@vm/shared';
import { family, M } from '../palette';
import { floorBoards, MapBuilder, OPENING_AUDIT, STAIR_AUDIT, stripes, type Brush, type DoorSpec, type Flight, type Rect, type Side } from './builder';

/**
 * Shared pieces for the hand-built maps (docs/maps/reference-maps.md): clapboard houses with an eave-less gable roof, interior walls and
 * furniture, awnings and street props. Everything is in voxel coordinates on ground level G (first air voxel, 3.2 m); the maps keep their own
 * metre-to-voxel conversion. Output must stay deterministic: a map's world hash is checked in its tests.
 */
export const G = 32;
export const TRIM = M('paint_white');
export const PETAL = [M('paint_pink'), M('paint_yellow'), M('paint_magenta'), M('paint_cyan'), M('paint_orange')];

/** Raised flower bed (voxels): brick rim, soil and a mix of flowers. */
export function planter(b: MapBuilder, x: number, z: number, wv: number, dv: number, colors: number[] = PETAL, y = G): void {
  b.box(x, y, z, x + wv, y + 5, z + dv, M('brick_red_3'));
  b.box(x + 1, y + 4, z + 1, x + wv - 1, y + 5, z + dv - 1, M('dirt_2'));
  for (let i = 0; i < wv - 2; i += 2) for (let j = 0; j < dv - 2; j += 2) b.box(x + 1 + i, y + 5, z + 1 + j, x + 2 + i, y + 7, z + 2 + j, colors[(i + j * 3) % colors.length]);
}

export function mailbox(b: MapBuilder, x: number, z: number, color: number): void {
  b.box(x, G, z, x + 1, G + 9, z + 1, M('log_1'));
  b.box(x - 1, G + 9, z - 1, x + 3, G + 13, z + 2, color);
  b.box(x + 3, G + 10, z - 1, x + 4, G + 11, z, M('paint_red'));
}

export function bin(b: MapBuilder, x: number, z: number, color: number): void {
  b.cylinder(x, z, G, G + 9, 3, color);
  b.cylinder(x, z, G + 9, G + 10, 3.5, M('steel_dark_1'));
}

export function lampPost(b: MapBuilder, x: number, z: number, glow: number): void {
  b.box(x, G, z, x + 2, G + 3, z + 2, M('stone_1'));
  b.box(x, G + 3, z, x + 1, G + 36, z + 1, M('paint_black'));
  b.box(x - 1, G + 36, z - 1, x + 2, G + 40, z + 2, glow);
  b.box(x - 1, G + 40, z - 1, x + 2, G + 41, z + 2, M('paint_black'));
}

/** Long school-bus style vehicle (26 wide, 30 tall); `alongX` false = running north-south. */
export function bus(b: MapBuilder, x: number, z: number, len: number, body: number, alongX: boolean): void {
  const W = 26;
  const H = 30;
  const [bx, bz] = alongX ? [len, W] : [W, len];
  b.box(x, G + 4, z, x + bx, G + H, z + bz, body);
  b.box(x, G + 14, z, x + bx, G + 17, z + bz, M('paint_black'));
  b.box(x, G + H, z + 1, x + bx, G + H + 1, z + bz - 1, M('chrome'));
  if (alongX) {
    for (let k = 8; k < len - 12; k += 10) for (const zz of [z, z + bz - 1]) b.box(x + k, G + 19, zz, x + k + 7, G + 26, zz + 1, M('glass_tint'));
    for (const xx of [x + 8, x + len - 18]) for (const zz of [z - 1, z + bz - 4]) b.box(xx, G, zz, xx + 9, G + 9, zz + 5, M('rubber'));
  } else {
    for (let k = 8; k < len - 12; k += 10) for (const xx of [x, x + bx - 1]) b.box(xx, G + 19, z + k, xx + 1, G + 26, z + k + 7, M('glass_tint'));
    for (const zz of [z + 8, z + len - 18]) for (const xx of [x - 1, x + bx - 4]) b.box(xx, G, zz, xx + 5, G + 9, zz + 9, M('rubber'));
  }
}

// Houses ----------------------------------------------------------------------------------------

export interface HouseSpec {
  /** Voxel box of the building (half-open). */
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  floors: number;
  wall: number;
  roof: number;
  roofStyle?: 'gable' | 'gableX' | 'flat' | 'pyramid' | 'none';
  doors: DoorSpec[];
  /** Floor height in voxels (default 30). */
  floorH?: number;
  /** Corner post material (default white trim). */
  post?: number;
  /** Material of the foundation, floor-line bands and flat-roof parapet (default white trim). */
  trim?: number;
  /** Material of the wall's bottom two courses (default stone_1). */
  plinth?: number;
  /** Half-timber beams: material and spacing in voxels (default none). */
  timber?: { mat: number; spacing: number };
  /** Window shape (default 10 wide, 12 tall, sill 8, every 24 voxels). */
  windows?: { w: number; h: number; sill: number; spacing: number } | null;
  /** Floor material or brush (default planks_1/planks_3 boards). */
  floor?: Brush;
  /** Height of the flat roof's parapet in voxels (default 3). */
  parapet?: number;
  /** Flat roofs only: a staircase from the top floor up to the roof deck (needs a building at least 7 m deep and 8 m long). */
  roofAccess?: boolean;
  /** Flat roofs: total height in voxels of a see-through railing (parapet base, posts every 4 voxels, solid top rail) round the roof edge. */
  railing?: number;
}

export interface Box {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  top: number;
  flights?: Flight[];
}

/**
 * Gable roof WITHOUT an eave. The world shader treats any column under a roof as covered (no direct sun, half the sky light), and the
 * eave of building()'s own gable hangs one voxel over the column in front of every wall, which leaves every wall about four times too
 * dark (peach read as brown). This roof stops at the wall line, so the walls stand in the light.
 */
export function gable(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, top: number, ridgeAlongX: boolean, roof: number, wall: number): void {
  const t = 2;
  const span = ridgeAlongX ? z1 - z0 : x1 - x0;
  const layers = Math.ceil(span / 4) + 1;
  b.box(x0, top, z0, x1, top + 1, z1, floorBoards([M('planks_1'), M('planks_3')], 3)); // attic floor
  for (let k = 0; k < layers; k++) {
    const y = top + 1 + k;
    if (ridgeAlongX) {
      const za = z0 + 2 * k;
      const zb = z1 - 2 * k;
      if (za >= zb) break;
      b.box(x0, y, za, x1, y + 1, Math.min(za + 3, zb), roof);
      b.box(x0, y, Math.max(zb - 3, za), x1, y + 1, zb, roof);
      b.box(x0, y, za + 3, x0 + t, y + 1, zb - 3, wall);
      b.box(x1 - t, y, za + 3, x1, y + 1, zb - 3, wall);
    } else {
      const xa = x0 + 2 * k;
      const xb = x1 - 2 * k;
      if (xa >= xb) break;
      b.box(xa, y, z0, Math.min(xa + 3, xb), y + 1, z1, roof);
      b.box(Math.max(xb - 3, xa), y, z0, xb, y + 1, z1, roof);
      b.box(xa + 3, y, z0, xb - 3, y + 1, z0 + t, wall);
      b.box(xa + 3, y, z1 - t, xb - 3, y + 1, z1, wall);
    }
  }
}

/** Clapboard house with trim and a stone base; returns its voxel box and roof top. */
export function house(b: MapBuilder, s: HouseSpec): Box {
  const { x0, x1, z0, z1 } = s;
  const fh = s.floorH ?? 30;
  const boards = s.floor ?? floorBoards([M('planks_1'), M('planks_3')], 3);
  const win = s.windows === undefined ? { w: 10, h: 12, sill: 8, spacing: 24 } : s.windows;
  const r = b.building({
    x0, x1, z0, z1, y0: G - 1, floors: s.floors, floorH: fh, wallT: 2, wall: s.wall, trim: s.trim ?? TRIM, band: s.trim ?? TRIM, roof: s.roof, roofAccess: s.roofAccess,
    roofStyle: s.roofStyle === 'flat' ? 'flat' : 'none', floor: boards, parapet: s.parapet ?? 3,
    windows: win ? { ...win, glass: M('glass') } : null,
    doors: s.doors,
  });
  b.box(x0 + 2, G - 1, z0 + 2, x1 - 2, G, z1 - 2, boards);
  // Corner posts and a plinth on the wall (only where there is wall: door openings stay open at the sill).
  for (const [cx, cz] of [[x0, z0], [x1 - 2, z0], [x0, z1 - 2], [x1 - 2, z1 - 2]]) b.box(cx, G, cz, cx + 2, G + fh * s.floors, cz + 2, s.post ?? TRIM);
  for (let y = G; y < G + 2; y++)
    for (let z = z0; z < z1; z++)
      for (let x = x0; x < x1; x++) {
        const edge = x < x0 + 2 || x >= x1 - 2 || z < z0 + 2 || z >= z1 - 2;
        if (edge && b.w.get(x, y, z) !== AIR) b.set(x, y, z, s.plinth ?? M('stone_1'));
      }
  if (s.roofAccess && s.roofStyle !== 'flat') throw new Error('house: roofAccess needs a flat roof');
  if (s.railing) {
    if (s.roofStyle !== 'flat') throw new Error('house: railing needs a flat roof');
    roofRailing(b, x0, z0, x1, z1, G + 1 + fh * s.floors, s.parapet ?? 3, s.railing, s.post ?? TRIM);
  }
  if (s.timber) timberFrame(b, x0, z0, x1, z1, G + 2, G + fh * s.floors, s.timber.mat, s.timber.spacing);
  const top = G - 1 + fh * s.floors;
  const style = s.roofStyle ?? 'gable';
  if (style === 'pyramid') pyramid(b, x0, z0, x1, z1, top, s.roof);
  else if (style === 'gable' || style === 'gableX') gable(b, x0, z0, x1, z1, top, style === 'gable' ? x1 - x0 >= z1 - z0 : false, s.roof, s.wall);
  return { x0, z0, x1, z1, top: r.top, flights: r.flights };
}

/**
 * See-through railing round a flat roof: a solid base (the parapet), posts every 4 voxels (gaps of 3 voxels are narrower than a player, so
 * nobody walks through, but bullets and eyes pass) and a solid top rail. `deckY` is the roof surface; `total` the height above it.
 */
export function roofRailing(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, deckY: number, base: number, total: number, mat: number): void {
  const ring: [number, number, number][] = [];
  for (let x = x0; x < x1; x++) ring.push([x, z0, x - x0], [x, z1 - 1, x - x0]);
  for (let z = z0 + 1; z < z1 - 1; z++) ring.push([x0, z, z - z0], [x1 - 1, z, z - z0]);
  for (const [x, z, along] of ring) {
    if (along % 4 === 0) b.box(x, deckY + base, z, x + 1, deckY + total, z + 1, mat);
    b.box(x, deckY + total - 1, z, x + 1, deckY + total, z + 1, mat);
  }
}

/** Half-timbering: replaces wall voxels with beams every `spacing` voxels (not over windows or doors, which are air or glass). */
export function timberFrame(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, y0: number, y1: number, mat: number, spacing: number): void {
  const glass = M('glass');
  for (let y = y0; y < y1; y++)
    for (let z = z0; z < z1; z++)
      for (let x = x0; x < x1; x++) {
        const onWall = x < x0 + 2 || x >= x1 - 2 || z < z0 + 2 || z >= z1 - 2;
        if (!onWall) continue;
        const along = x < x0 + 2 || x >= x1 - 2 ? z - z0 : x - x0;
        if (along % spacing >= 2) continue;
        const v = b.w.get(x, y, z);
        if (v !== AIR && v !== glass && v !== TRIM) b.set(x, y, z, mat);
      }
}

/** Stepped pyramid roof with no eave (see gable() for why). */
export function pyramid(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, top: number, roof: number): void {
  b.box(x0, top, z0, x1, top + 1, z1, floorBoards([M('planks_1'), M('planks_3')], 3));
  for (let k = 0; ; k++) {
    const xa = x0 + 2 * k;
    const xb = x1 - 2 * k;
    const za = z0 + 2 * k;
    const zb = z1 - 2 * k;
    if (xa >= xb || za >= zb) break;
    b.box(xa, top + 1 + k, za, xb, top + 2 + k, zb, roof);
  }
}

/** Interior partition wall with a doorway (voxels). `axis` is the direction the wall runs; `at` its position across; floor 0 or 1. */
export function partition(b: MapBuilder, floor: number, axis: 'x' | 'z', at: number, from: number, to: number, doorAt: number, doorW = 16): void {
  const y = G + floor * 30;
  const wall = M('plaster_3');
  if (axis === 'z') {
    b.box(at, y, from, at + 2, y + 29, to, wall);
    b.clear(at, y, doorAt, at + 2, y + 26, doorAt + doorW);
    b.box(at, y + 26, doorAt, at + 2, y + 27, doorAt + doorW, TRIM);
  } else {
    b.box(from, y, at, to, y + 29, at + 2, wall);
    b.clear(doorAt, y, at, doorAt + doorW, y + 26, at + 2);
    b.box(doorAt, y + 26, at, doorAt + doorW, y + 27, at + 2, TRIM);
  }
}

export function sofa(b: MapBuilder, x: number, y: number, z: number, color: number): void {
  b.box(x, y, z, x + 26, y + 5, z + 9, color);
  b.box(x, y + 5, z, x + 26, y + 11, z + 2, color);
  b.box(x, y + 5, z, x + 2, y + 8, z + 9, color);
  b.box(x + 24, y + 5, z, x + 26, y + 8, z + 9, color);
}

export function table(b: MapBuilder, x: number, y: number, z: number, w: number, d: number): void {
  b.box(x, y + 7, z, x + w, y + 8, z + d, M('planks_dark_1'));
  for (const [a, c] of [[0, 0], [w - 1, 0], [0, d - 1], [w - 1, d - 1]]) b.box(x + a, y, z + c, x + a + 1, y + 7, z + c + 1, M('planks_dark_2'));
}

export function bed(b: MapBuilder, x: number, y: number, z: number, alongX: boolean, color: number): void {
  const [lx, lz] = alongX ? [20, 12] : [12, 20];
  b.box(x, y, z, x + lx, y + 4, z + lz, M('planks_dark_1'));
  b.box(x + 1, y + 4, z + 1, x + lx - 1, y + 6, z + lz - 1, color);
  if (alongX) b.box(x, y + 4, z, x + 2, y + 10, z + lz, M('planks_dark_1'));
  else b.box(x, y + 4, z, x + lx, y + 10, z + 2, M('planks_dark_1'));
  if (alongX) b.box(x + 2, y + 6, z + 1, x + 6, y + 8, z + lz - 1, M('cloth_white'));
  else b.box(x + 1, y + 6, z + 2, x + lx - 1, y + 8, z + 6, M('cloth_white'));
}

export function counter(b: MapBuilder, x: number, y: number, z: number, lx: number, lz: number): void {
  b.box(x, y, z, x + lx, y + 9, z + lz, M('paint_white'));
  b.box(x, y + 9, z, x + lx, y + 10, z + lz, M('stone_3'));
}

export function shelf(b: MapBuilder, x: number, y: number, z: number, lx: number, lz: number, h = 20): void {
  b.box(x, y, z, x + lx, y + h, z + lz, M('planks_dark_1'));
  for (let k = 2; k < h; k += 5) {
    if (lx > lz) b.box(x + 1, y + k, z, x + lx - 1, y + k + 2, z + 1, M('paint_orange'));
    else b.box(x, y + k, z + 1, x + 1, y + k + 2, z + lz - 1, M('paint_teal'));
  }
}

/** Striped awning over a door on side `side` of the box, centred at `at` (voxels along the wall), `w` wide. */
export function awning(b: MapBuilder, side: Side, box: Box, at: number, w: number, color: number): void {
  const y = G + 24;
  if (side === 'e' || side === 'w') {
    const xa = side === 'e' ? box.x1 : box.x0 - 10;
    b.box(xa, y, at - 2, xa + 10, y + 1, at + w + 2, stripes(color, M('cloth_white'), 3, 'z'));
    const post = side === 'e' ? xa + 9 : xa;
    b.box(post, G, at - 1, post + 1, y, at, TRIM);
    b.box(post, G, at + w, post + 1, y, at + w + 1, TRIM);
    b.box(xa, G - 1, at - 2, xa + 10, G, at + w + 2, M('concrete_3'));
  } else {
    const za = side === 's' ? box.z1 : box.z0 - 10;
    b.box(at - 2, y, za, at + w + 2, y + 1, za + 10, stripes(color, M('cloth_white'), 3, 'x'));
    const post = side === 's' ? za + 9 : za;
    b.box(at - 1, G, post, at, y, post + 1, TRIM);
    b.box(at + w, G, post, at + w + 1, y, post + 1, TRIM);
    b.box(at - 2, G - 1, za, at + w + 2, G, za + 10, M('concrete_3'));
  }
}

// Furnishing ------------------------------------------------------------------------------------

/**
 * Plan rectangles (voxels, [x0, z0, x1, z1]) that furniture on floor `floor` of a house must keep clear of: the foot, steps and head room of the flights
 * that start on this floor, and the landing and slab opening of the flights that arrive on it, each grown by `pad`.
 */
export function stairKeepClear(box: Box, floor: number, pad = 8): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  const grow = (r: Rect): [number, number, number, number] => [r.x0 - pad, r.z0 - pad, r.x1 + pad, r.z1 + pad];
  for (const fl of box.flights ?? []) {
    if (fl.floor === floor) {
      out.push(grow(fl.foot), grow(fl.steps), grow(fl.open));
    } else if (fl.floor === floor - 1 && !fl.roof) out.push(grow(fl.exit), grow(fl.open));
  }
  return out;
}

export interface Room {
  /** Inner floor bounds in voxels (half-open): inside the walls. */
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  /** Floor level (first air voxel above the floor). */
  y: number;
}

export interface Piece {
  w: number;
  d: number;
  draw: (x: number, z: number) => void;
}

const CLEAR = 12; // voxels of walkway kept around every piece that stands near a door

/** True if a w x d footprint at (x, z) is free of solid voxels, clear of the avoid boxes and not within CLEAR voxels of any opening in the room's walls. */
function spotOk(b: MapBuilder, room: Room, x: number, z: number, w: number, d: number, avoid: [number, number, number, number][]): boolean {
  if (x < room.x0 || z < room.z0 || x + w > room.x1 || z + d > room.z1) return false;
  for (const [ax0, az0, ax1, az1] of avoid) if (x < ax1 && x + w > ax0 && z < az1 && z + d > az0) return false;
  for (let zz = z; zz < z + d; zz++)
    for (let xx = x; xx < x + w; xx++) if (b.w.get(xx, room.y, zz) !== AIR || b.w.get(xx, room.y + 6, zz) !== AIR || b.w.get(xx, room.y - 1, zz) === AIR) return false;
  // Openings: air in the wall band (two voxels outside the room bounds) at knee height.
  for (let zz = z - CLEAR; zz < z + d + CLEAR; zz++)
    for (let xx = x - CLEAR; xx < x + w + CLEAR; xx++) {
      const inRoom = xx >= room.x0 && xx < room.x1 && zz >= room.z0 && zz < room.z1;
      if (inRoom) continue;
      const nearBand = xx >= room.x0 - 2 && xx < room.x1 + 2 && zz >= room.z0 - 2 && zz < room.z1 + 2;
      if (nearBand && b.w.get(xx, room.y + 1, zz) === AIR) return false;
    }
  return true;
}

/**
 * Puts as many of the pieces as fit along the walls of a room, trying positions round the perimeter in a fixed order (offset by `seed`).
 * Pieces never touch other solids, never stand near a door and never sit in an `avoid` box (stairs). Returns how many were placed.
 */
export function scatter(b: MapBuilder, room: Room, pieces: Piece[], seed = 0, avoid: [number, number, number, number][] = []): number {
  const spots: [number, number][] = [];
  const step = 8;
  for (let x = room.x0; x < room.x1; x += step) spots.push([x, room.z0], [x, room.z1]);
  for (let z = room.z0; z < room.z1; z += step) spots.push([room.x0, z], [room.x1, z]);
  const n = spots.length;
  let placed = 0;
  let k = seed % Math.max(1, n);
  for (const piece of pieces) {
    for (let tries = 0; tries < n; tries++, k = (k + 5) % n) {
      const [sx, sz] = spots[k];
      // Snap the piece's footprint against the wall the spot is on.
      const x = sx >= room.x1 ? room.x1 - piece.w : sx;
      const z = sz >= room.z1 ? room.z1 - piece.d : sz;
      if (!spotOk(b, room, x, z, piece.w, piece.d, avoid)) continue;
      piece.draw(x, z);
      placed++;
      k = (k + 5) % n;
      break;
    }
  }
  return placed;
}

export function pew(b: MapBuilder, x: number, y: number, z: number, len: number): void {
  b.box(x, y, z, x + len, y + 5, z + 6, M('planks_dark_1'));
  b.box(x, y + 5, z + 5, x + len, y + 12, z + 6, M('planks_dark_1'));
  b.box(x, y, z, x + 1, y + 12, z + 6, M('planks_dark_2'));
  b.box(x + len - 1, y, z, x + len, y + 12, z + 6, M('planks_dark_2'));
}

/** Tavern bar: a counter with a shelf of bottles behind it. `lx` x `lz` voxels. */
export function bar(b: MapBuilder, x: number, y: number, z: number, lx: number, lz: number): void {
  b.box(x, y, z, x + lx, y + 10, z + lz, M('planks_dark_1'));
  b.box(x, y + 10, z, x + lx, y + 11, z + lz, M('planks_3'));
  const alongX = lx >= lz;
  const row = alongX ? [x + 2, z - 1] : [x - 1, z + 2];
  for (let i = 0; i < (alongX ? lx : lz) - 4; i += 4) {
    const bx = alongX ? row[0] + i : row[0];
    const bz = alongX ? row[1] : row[1] + i;
    b.box(bx, y + 11, bz, bx + 1, y + 15, bz + 1, [M('paint_green'), M('paint_orange'), M('paint_red')][(i >> 2) % 3]);
  }
}

/** Two-wheeled hay cart, 30 x 12 voxels, loaded with bales. `alongX` false = long side along z. */
export function cart(b: MapBuilder, x: number, z: number, alongX: boolean): void {
  const [lx, lz] = alongX ? [30, 12] : [12, 30];
  b.box(x, G + 4, z, x + lx, G + 6, z + lz, M('planks_dark_1'));
  b.box(x, G + 6, z, x + lx, G + 9, z + 1, M('planks_dark_2'));
  b.box(x, G + 6, z + lz - 1, x + lx, G + 9, z + lz, M('planks_dark_2'));
  b.box(x, G + 6, z, x + 1, G + 9, z + lz, M('planks_dark_2'));
  b.box(x + lx - 1, G + 6, z, x + lx, G + 9, z + lz, M('planks_dark_2'));
  b.box(x + 1, G + 6, z + 1, x + lx - 1, G + 13, z + lz - 1, M('hay'));
  if (alongX) for (const wxx of [x + 7, x + lx - 10]) for (const wzz of [z - 2, z + lz]) b.cylinder(wxx + 1, wzz, G, G + 9, 4.2, M('planks_dark_2'), 2.4);
  else for (const wzz of [z + 7, z + lz - 10]) for (const wxx of [x - 2, x + lx]) b.cylinder(wxx, wzz + 1, G, G + 9, 4.2, M('planks_dark_2'), 2.4);
  // Shafts.
  if (alongX) b.box(x - 14, G + 5, z + 5, x, G + 6, z + 7, M('planks_dark_2'));
  else b.box(x + 5, G + 5, z - 14, x + 7, G + 6, z, M('planks_dark_2'));
}

// Boundary and region -----------------------------------------------------------------------------

/** A closed polygon in play-space metres: point-in-polygon and distance-to-edge, used to keep props off the boundary. */
export class Region {
  constructor(readonly outline: [number, number][]) {}

  inside(x: number, z: number): boolean {
    const o = this.outline;
    let c = false;
    for (let i = 0, j = o.length - 1; i < o.length; j = i++) {
      const [xi, zi] = o[i];
      const [xj, zj] = o[j];
      if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
    }
    return c;
  }

  /** Distance in metres from a point to the nearest edge. */
  edgeDistance(x: number, z: number): number {
    const o = this.outline;
    let best = 1e18;
    for (let i = 0, j = o.length - 1; i < o.length; j = i++) {
      const [ax, az] = o[j];
      const [bx, bz] = o[i];
      const dx = bx - ax;
      const dz = bz - az;
      const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz)));
      const ex = ax + dx * t - x;
      const ez = az + dz * t - z;
      best = Math.min(best, ex * ex + ez * ez);
    }
    return Math.sqrt(best);
  }
}

/**
 * Exact, fast `inside` and `distance` over voxel columns for a Region. Region.inside / edgeDistance loop over every outline edge per call, which costs
 * seconds when a map asks for every column of a large world. This precomputes the polygon's crossings per voxel row (so `inside` is a lookup over a
 * few numbers, with the same even-odd rule as Region.inside) and buckets the edges in 4 m cells that keep only the edges within `maxDist` (+ the cell's
 * half diagonal) of the cell, so `distance` is exact up to `maxDist` metres and returns maxDist + 1 beyond it.
 */
export class DistanceField {
  private readonly rows = new Map<number, number[]>();
  private readonly cells = new Map<number, number[]>();
  private readonly cell = 40; // voxels
  readonly far: number;
  constructor(readonly region: Region, readonly ox: number, readonly oz: number, readonly maxDist: number) {
    this.far = maxDist + 1;
  }

  private crossings(Z: number): number[] {
    let c = this.rows.get(Z);
    if (c) return c;
    c = [];
    const z = (Z - this.oz + 0.5) / 10;
    const o = this.region.outline;
    for (let i = 0, j = o.length - 1; i < o.length; j = i++) {
      const [xi, zi] = o[i];
      const [xj, zj] = o[j];
      if (zi > z !== zj > z) c.push(((xj - xi) * (z - zi)) / (zj - zi) + xi);
    }
    c.sort((a, b) => a - b);
    this.rows.set(Z, c);
    return c;
  }

  /** Same result as region.inside((X - ox + 0.5) / 10, (Z - oz + 0.5) / 10). */
  inside(X: number, Z: number): boolean {
    const x = (X - this.ox + 0.5) / 10;
    let odd = false;
    for (const cx of this.crossings(Z)) if (x < cx) odd = !odd;
    return odd;
  }

  /** Same result as region.edgeDistance(...) when that is <= maxDist, else far. */
  distance(X: number, Z: number): number {
    const key = Math.floor(X / this.cell) * 100000 + Math.floor(Z / this.cell);
    let edges = this.cells.get(key);
    if (!edges) {
      edges = [];
      const cx = ((Math.floor(X / this.cell) + 0.5) * this.cell - this.ox) / 10;
      const cz = ((Math.floor(Z / this.cell) + 0.5) * this.cell - this.oz) / 10;
      const reach = this.maxDist + (this.cell / 10) * 0.75; // half diagonal is 0.707 cells
      const o = this.region.outline;
      for (let i = 0, j = o.length - 1; i < o.length; j = i++) {
        const [ax, az] = o[j];
        const [bx, bz] = o[i];
        const dx = bx - ax;
        const dz = bz - az;
        const t = Math.max(0, Math.min(1, ((cx - ax) * dx + (cz - az) * dz) / (dx * dx + dz * dz)));
        const ex = ax + dx * t - cx;
        const ez = az + dz * t - cz;
        if (Math.sqrt(ex * ex + ez * ez) <= reach) edges.push(i);
      }
      this.cells.set(key, edges);
    }
    const x = (X - this.ox + 0.5) / 10;
    const z = (Z - this.oz + 0.5) / 10;
    const o = this.region.outline;
    let best = 1e18;
    for (const i of edges) {
      const j = i === 0 ? o.length - 1 : i - 1;
      const [ax, az] = o[j];
      const [bx, bz] = o[i];
      const dx = bx - ax;
      const dz = bz - az;
      const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz)));
      const ex = ax + dx * t - x;
      const ez = az + dz * t - z;
      best = Math.min(best, ex * ex + ez * ez);
    }
    const d = Math.sqrt(best);
    return d <= this.maxDist ? d : this.far;
  }
}

/**
 * Limits play to the inside of a Region (the boundary wall is built outside it): nothing that moves can pass its outline or stand on the wall. The whole footprint of
 * every building (play metres) is always in play, even where the outline cuts a corner off it: a building is carved out of the boundary wall there, and its rooms and
 * stairs must stay usable.
 */
export function regionPlay(b: MapBuilder, region: Region, ox: number, oz: number, buildings: Footprint[] = []): void {
  const df = new DistanceField(region, ox, oz, 0.01);
  const rects = buildings.map((f) => [ox + Math.floor(f.x0 * 10), oz + Math.floor(f.z0 * 10), ox + Math.ceil(f.x1 * 10), oz + Math.ceil(f.z1 * 10)]);
  b.limitPlay((X, Z) => df.inside(X, Z) || rects.some(([x0, z0, x1, z1]) => X >= x0 && X < x1 && Z >= z0 && Z < z1));
}

export interface BandSpec {
  /** Voxel position of the play-space origin. */
  ox: number;
  oz: number;
  /** Metres of wall outside the outline (default 4). */
  band?: number;
  /** Only columns with voxel x below this are built (a mirrored map builds half). Default: whole world. */
  xMax?: number;
  /** Height of the wall in voxels above ground. */
  height: number;
  /** Material of the first `skirtH` voxels of the visible face and of the face above it. */
  skirt: Brush;
  skirtH: number;
  face: Brush;
  /** Tough core (leaf blocks are one-hit, so a soft wall could be shot through) and the top two layers. */
  core: number;
  cap: Brush;
}

/**
 * A wall band all round a region: a tough rock core with a visible face (skirt plus face material) on the side the players see and on the
 * outer side, so the wall reads the same from the ground and from the air.
 */
export function boundaryBand(b: MapBuilder, region: Region, spec: BandSpec, field?: DistanceField): void {
  const band = spec.band ?? 4;
  const df = field ?? new DistanceField(region, spec.ox, spec.oz, band + 0.01);
  const xMax = Math.min(spec.xMax ?? b.sx, b.sx);
  let zMax = 0;
  let xMin = 1e9;
  let zMin = 1e9;
  let xHi = 0;
  for (const [x, z] of region.outline) {
    zMax = Math.max(zMax, z);
    zMin = Math.min(zMin, z);
    xMin = Math.min(xMin, x);
    xHi = Math.max(xHi, x);
  }
  const m = Math.ceil(band * 10) + 5;
  const X0 = Math.max(0, spec.ox + Math.floor(xMin * 10) - m);
  const X1 = Math.min(xMax, spec.ox + Math.ceil(xHi * 10) + m);
  const Z0 = Math.max(0, spec.oz + Math.floor(zMin * 10) - m);
  const Z1 = Math.min(b.sz, spec.oz + Math.ceil(zMax * 10) + m);
  for (let X = X0; X < X1; X++) {
    for (let Z = Z0; Z < Z1; Z++) {
      if (df.inside(X, Z)) continue;
      const d = df.distance(X, Z);
      if (d > band) continue;
      if (d <= 0.45) {
        b.box(X, G, Z, X + 1, G + spec.skirtH, Z + 1, spec.skirt);
        b.box(X, G + spec.skirtH, Z, X + 1, G + spec.height, Z + 1, spec.face);
      } else if (d >= band - 0.8) b.box(X, G, Z, X + 1, G + spec.height, Z + 1, spec.face);
      else {
        b.box(X, G, Z, X + 1, G + spec.height - 2, Z + 1, spec.core);
        b.box(X, G + spec.height - 2, Z, X + 1, G + spec.height, Z + 1, spec.cap);
      }
    }
  }
}

/** Deterministic scatter over a region's boundary band: calls `fn(X, Z)` for roughly one column in `every` that lies `d0`..`d1` metres outside. */
export function bandScatter(region: Region, ox: number, oz: number, d0: number, d1: number, every: number, seed: number, w: number, h: number, fn: (X: number, Z: number) => void, field?: DistanceField): void {
  const df = field ?? new DistanceField(region, ox, oz, d1 + 0.01);
  for (let Z = 0; Z < h; Z++)
    for (let X = 0; X < w; X++) {
      if (hash32(X, Z, 0, seed) % every !== 0) continue;
      if (df.inside(X, Z)) continue;
      const d = df.distance(X, Z);
      if (d < d0 || d > d1) continue;
      fn(X, Z);
    }
}

/** True if every column of the voxel box is air at ground level up to 2 voxels above it (a spot a prop may stand on). */
export function freeGround(b: MapBuilder, x0: number, z0: number, x1: number, z1: number): boolean {
  for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) if (b.w.get(x, G, z) !== AIR || b.w.get(x, G + 1, z) !== AIR) return false;
  return true;
}

// Furniture as pieces for scatter() -----------------------------------------------------------------

export const pieces = {
  sofa: (b: MapBuilder, y: number, color: number): Piece => ({ w: 26, d: 9, draw: (x, z) => sofa(b, x, y, z, color) }),
  table: (b: MapBuilder, y: number, w: number, d: number): Piece => ({ w, d, draw: (x, z) => table(b, x, y, z, w, d) }),
  bed: (b: MapBuilder, y: number, color: number): Piece => ({ w: 20, d: 12, draw: (x, z) => bed(b, x, y, z, true, color) }),
  counter: (b: MapBuilder, y: number, lx: number, lz: number): Piece => ({ w: lx, d: lz, draw: (x, z) => counter(b, x, y, z, lx, lz) }),
  shelfX: (b: MapBuilder, y: number, len: number, h = 20): Piece => ({ w: len, d: 4, draw: (x, z) => shelf(b, x, y, z, len, 4, h) }),
  shelfZ: (b: MapBuilder, y: number, len: number, h = 20): Piece => ({ w: 4, d: len, draw: (x, z) => shelf(b, x, y, z, 4, len, h) }),
  crate: (b: MapBuilder, y: number, size = 8): Piece => ({ w: size, d: size, draw: (x, z) => b.crate(x, y, z, size) }),
  barrel: (b: MapBuilder, y: number, color: number): Piece => ({ w: 8, d: 8, draw: (x, z) => b.barrel(x + 4, y, z + 4, color) }),
  hay: (b: MapBuilder, y: number): Piece => ({ w: 12, d: 8, draw: (x, z) => b.haybale(x, y, z, true) }),
  hayStack: (b: MapBuilder, y: number): Piece => ({
    w: 12,
    d: 8,
    draw: (x, z) => {
      b.haybale(x, y, z, true);
      b.haybale(x, y + 8, z, true);
    },
  }),
  pew: (b: MapBuilder, y: number, len: number): Piece => ({ w: len, d: 6, draw: (x, z) => pew(b, x, y, z, len) }),
  bar: (b: MapBuilder, y: number, lx: number, lz: number): Piece => ({ w: lx, d: lz, draw: (x, z) => bar(b, x, y, z, lx, lz) }),
};

// Outdoor props ---------------------------------------------------------------------------------------

/** Festival bunting: a drooping string of flags between two points (voxels), sagging `sag` voxels at the middle. */
export function bunting(b: MapBuilder, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, colors: number[], sag = 4): void {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(z1 - z0));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const x = Math.round(x0 + (x1 - x0) * t);
    const z = Math.round(z0 + (z1 - z0) * t);
    const y = Math.round(y0 + (y1 - y0) * t - sag * 4 * t * (1 - t));
    b.set(x, y, z, M('log_2'));
    if (i % 3 === 1) {
      b.set(x, y - 1, z, colors[(i / 3 | 0) % colors.length]);
      b.set(x, y - 2, z, colors[(i / 3 | 0) % colors.length]);
    }
  }
}

/** Stone village well with a timber frame and a little roof. */
export function well(b: MapBuilder, cx: number, cz: number, roof: number): void {
  const stone = M('stone_3');
  b.cylinder(cx, cz, G, G + 8, 9, stone);
  b.cylinder(cx, cz, G + 4, G + 8, 6, AIR);
  b.cylinder(cx, cz, G, G + 5, 6, M('water'));
  for (const [dx, dz] of [[-8, 0], [8, 0]]) b.box(cx + dx - 1, G + 8, cz + dz - 1, cx + dx + 1, G + 30, cz + dz + 1, M('planks_dark_1'));
  b.box(cx - 9, G + 28, cz - 1, cx + 9, G + 30, cz + 1, M('planks_dark_1'));
  for (let k = 0; k < 6; k++) b.box(cx - 10 + k * 2, G + 30 + k, cz - 5 + k, cx + 10 - k * 2, G + 31 + k, cz + 5 - k, roof);
  b.box(cx - 1, G + 21, cz - 1, cx + 1, G + 28, cz + 1, M('log_2'));
}

/** Scarecrow on a post with a straw hat, arms out. */
export function scarecrow(b: MapBuilder, x: number, z: number): void {
  b.box(x, G, z, x + 2, G + 22, z + 2, M('planks_dark_1'));
  b.box(x - 7, G + 15, z, x + 9, G + 17, z + 2, M('planks_dark_1'));
  b.box(x - 1, G + 8, z - 1, x + 3, G + 17, z + 3, M('cloth_blue'));
  b.box(x - 1, G + 17, z - 1, x + 3, G + 21, z + 3, M('hay'));
  b.box(x - 3, G + 21, z - 3, x + 5, G + 22, z + 5, M('wheat'));
  b.box(x, G + 22, z, x + 2, G + 25, z + 2, M('wheat'));
  b.box(x - 8, G + 14, z, x - 6, G + 15, z + 2, M('hay'));
  b.box(x + 8, G + 14, z, x + 10, G + 15, z + 2, M('hay'));
}

/** A heap of pumpkins: a base layer with a few stacked on top. */
export function pumpkins(b: MapBuilder, x: number, z: number, nx: number, nz: number): void {
  const p = M('pumpkin');
  for (let i = 0; i < nx; i++)
    for (let j = 0; j < nz; j++) {
      const bx = x + i * 6;
      const bz = z + j * 6;
      b.box(bx, G, bz, bx + 5, G + 4, bz + 5, p);
      b.box(bx + 2, G + 4, bz + 2, bx + 3, G + 6, bz + 3, M('leaves_3'));
      if ((i + j) % 2 === 0) b.box(bx + 1, G + 4, bz + 1, bx + 4, G + 7, bz + 4, p);
    }
}

/** Lantern post: timber post with a glowing orange lantern and a little hood. */
export function lantern(b: MapBuilder, x: number, z: number, height = 34): void {
  b.box(x, G, z, x + 2, G + height, z + 2, M('planks_dark_1'));
  b.box(x - 1, G + height, z - 1, x + 3, G + height + 4, z + 3, M('neon_orange'));
  b.box(x - 2, G + height + 4, z - 2, x + 4, G + height + 5, z + 4, M('paint_black'));
  b.box(x, G + height - 6, z + 2, x + 6, G + height - 5, z + 3, M('planks_dark_1'));
}
void family;

// City and neon ---------------------------------------------------------------------------------------

/** 5 x 5 pixel letters for neon signs (rows top to bottom, '#' = lit). */
const FONT: Record<string, string[]> = {
  A: [' ### ', '#   #', '#####', '#   #', '#   #'], B: ['#### ', '#   #', '#### ', '#   #', '#### '], C: [' ####', '#    ', '#    ', '#    ', ' ####'],
  D: ['#### ', '#   #', '#   #', '#   #', '#### '], E: ['#####', '#    ', '#### ', '#    ', '#####'], F: ['#####', '#    ', '#### ', '#    ', '#    '],
  G: [' ####', '#    ', '#  ##', '#   #', ' ####'], H: ['#   #', '#   #', '#####', '#   #', '#   #'], I: ['#####', '  #  ', '  #  ', '  #  ', '#####'],
  K: ['#   #', '#  # ', '###  ', '#  # ', '#   #'], L: ['#    ', '#    ', '#    ', '#    ', '#####'], M: ['#   #', '## ##', '# # #', '#   #', '#   #'],
  N: ['#   #', '##  #', '# # #', '#  ##', '#   #'], O: [' ### ', '#   #', '#   #', '#   #', ' ### '], P: ['#### ', '#   #', '#### ', '#    ', '#    '],
  R: ['#### ', '#   #', '#### ', '#  # ', '#   #'], S: [' ####', '#    ', ' ### ', '    #', '#### '], T: ['#####', '  #  ', '  #  ', '  #  ', '  #  '],
  U: ['#   #', '#   #', '#   #', '#   #', ' ### '], W: ['#   #', '#   #', '# # #', '## ##', '#   #'], Y: ['#   #', ' # # ', '  #  ', '  #  ', '  #  '],
  Z: ['#####', '   # ', '  #  ', ' #   ', '#####'],
};

/** Width in voxels of a sign made by signText. */
export function signWidth(text: string, px: number): number {
  return text.length * 6 * px - px;
}

/** Neon text on a vertical plane (see MapBuilder.glyph for the meaning of `face` and the corner); letters are 5 x px voxels tall. */
export function signText(b: MapBuilder, text: string, x: number, y: number, z: number, face: Side, color: number, px = 2): void {
  const rows: string[] = ['', '', '', '', ''];
  for (const ch of text) {
    const g = FONT[ch];
    for (let r = 0; r < 5; r++) rows[r] += (g ? g[r] : '     ') + ' ';
  }
  b.glyph(x, y, z, face, rows.map((r) => r.trimEnd()), { '#': color }, px, 1);
}

/** A glowing outline round the top of a flat-roofed building (the parapet's top edge). */
export function neonEdge(b: MapBuilder, box: { x0: number; z0: number; x1: number; z1: number; top: number }, y: number, color: number): void {
  b.walls(box.x0, y, box.z0, box.x1, y + 1, box.z1, 1, color);
}

/**
 * A row of 4 x 4 light panels along the centre line of a room, one every `every` voxels, touching the slab above (`ly` is the lowest air layer under it).
 * Skips any spot that is not open air under a solid ceiling, and any within `pad` voxels of a stair opening or flight of this floor, so a light never hangs
 * into the head room of a staircase.
 */
export function ceilingStrip(
  b: MapBuilder, room: { x0: number; z0: number; x1: number; z1: number }, ly: number, colors: number[], keepClear: [number, number, number, number][], every = 26, seed = 0,
): void {
  const z = (room.z0 + room.z1) >> 1;
  for (let x = room.x0 + 14; x < room.x1 - 10; x += every) {
    if (keepClear.some(([x0, z0, x1, z1]) => x + 4 > x0 - 4 && x < x1 + 4 && z + 4 > z0 - 4 && z < z1 + 4)) continue;
    if (b.w.get(x, ly, z) === AIR && b.w.get(x, ly + 1, z) !== AIR) b.box(x, ly, z, x + 4, ly + 1, z + 4, colors[(x + z + seed) % colors.length]);
  }
}

/** Emissive ceiling light panel (4 x 4 voxels) hanging against the ceiling of the room whose floor is at `floorY`. */
export function ceilingLight(b: MapBuilder, x: number, z: number, floorY: number, color: number): void {
  b.box(x, floorY + 27, z, x + 4, floorY + 28, z + 4, color);
}

/** Arcade cabinet (facing +z): black body, glowing screen and a coloured marquee, 9 x 7 x 18 voxels. */
export function cabinet(b: MapBuilder, x: number, y: number, z: number, color: number): void {
  b.box(x, y, z, x + 9, y + 18, z + 7, M('paint_black'));
  b.box(x + 1, y + 9, z + 7, x + 8, y + 15, z + 8, color);
  b.box(x + 1, y + 6, z + 7, x + 8, y + 8, z + 9, M('steel_dark_1'));
  b.box(x, y + 18, z, x + 9, y + 20, z + 7, color);
}

export function dumpster(b: MapBuilder, x: number, z: number, alongX: boolean): void {
  const [lx, lz] = alongX ? [18, 9] : [9, 18];
  b.box(x, G + 2, z, x + lx, G + 10, z + lz, M('paint_green'));
  b.box(x - 1, G + 10, z - 1, x + lx + 1, G + 11, z + lz + 1, M('steel_dark_1'));
  b.box(x + 1, G, z + 1, x + 3, G + 2, z + 3, M('rubber'));
  b.box(x + lx - 3, G, z + lz - 3, x + lx - 1, G + 2, z + lz - 1, M('rubber'));
}

/** Concrete jersey barrier, `len` voxels long. */
export function barrier(b: MapBuilder, x: number, z: number, len: number, alongX: boolean): void {
  const [lx, lz] = alongX ? [len, 6] : [6, len];
  b.box(x, G, z, x + lx, G + 4, z + lz, M('concrete_2'));
  if (alongX) b.box(x + 1, G + 4, z + 1, x + lx - 1, G + 9, z + lz - 1, M('concrete_2'));
  else b.box(x + 1, G + 4, z + 1, x + lx - 1, G + 9, z + lz - 1, M('concrete_2'));
  b.box(x, G + 3, z, x + lx, G + 4, z + lz, M('road_yellow'));
}

/** Bus shelter with a glowing ad panel; open on the +z side when `faceSouth`. */
export function busStop(b: MapBuilder, x: number, z: number, glow: number): void {
  b.box(x, G, z, x + 26, G + 1, z + 9, M('concrete_3'));
  b.box(x, G + 1, z, x + 26, G + 20, z + 1, M('glass_tint'));
  b.box(x, G + 20, z, x + 26, G + 22, z + 9, M('steel_dark_1'));
  for (const xx of [x, x + 25]) b.box(xx, G + 1, z, xx + 1, G + 20, z + 9, M('steel_dark_1'));
  b.box(x + 3, G + 4, z + 1, x + 13, G + 17, z + 2, glow);
  b.box(x + 16, G + 1, z + 3, x + 24, G + 3, z + 6, M('planks_dark_1'));
}

/**
 * A dark skyline outside the play area: towers with lit windows on a coarse grid, between `d0` and `d1` metres outside the region. The
 * windows are a hash pattern, so it is deterministic.
 */
export function skyline(
  b: MapBuilder, region: Region, ox: number, oz: number, d0: number, d1: number, seed: number, wallMat: number, lit: number[], hMin: number, hSpan: number,
): void {
  const cell = 60;
  for (let cz = 0; cz < b.sz; cz += cell)
    for (let cx = 0; cx < b.sx; cx += cell) {
      const h = hash32(cx, cz, 0, seed);
      if (h % 3 === 0) continue;
      const w = 30 + (h >> 4) % 20;
      const d = 30 + (h >> 9) % 20;
      const x0 = cx + (h >> 13) % 10;
      const z0 = cz + (h >> 17) % 10;
      const xm = (x0 + w / 2 - ox) / 10;
      const zm = (z0 + d / 2 - oz) / 10;
      if (region.inside(xm, zm)) continue;
      const dist = region.edgeDistance(xm, zm) - Math.max(w, d) / 20;
      if (dist < d0 || dist > d1) continue;
      const height = hMin + (h >> 21) % hSpan;
      const colors = lit;
      b.box(x0, G, z0, x0 + w, G + height, z0 + d, (x, y, z) => (hash32(x >> 1, y >> 2, z >> 1, seed + 5) % 6 === 0 ? colors[hash32(x >> 3, y >> 3, z >> 3, seed) % colors.length] : wallMat));
      b.box(x0, G + height, z0, x0 + w, G + height + 1, z0 + d, colors[h % colors.length]);
    }
}

// Doors and shop signs -------------------------------------------------------------------------------

export interface Footprint {
  name?: string;
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

/**
 * Doors for a building (footprints in play metres): one centred door on every side where open ground lies 1.6 m outside the wall, so a door
 * never opens onto a neighbour's wall or the boundary. Throws if no side is open.
 */
export function doorsFor(bd: Footprint, all: Footprint[], region: Region, tag: string): DoorSpec[] {
  const cx = (bd.x0 + bd.x1) / 2;
  const cz = (bd.z0 + bd.z1) / 2;
  const sides: [Side, number, number][] = [
    ['n', cx, bd.z0 - 1.6],
    ['s', cx, bd.z1 + 1.6],
    ['w', bd.x0 - 1.6, cz],
    ['e', bd.x1 + 1.6, cz],
  ];
  const out: DoorSpec[] = [];
  for (const [side, x, z] of sides) {
    if (!region.inside(x, z) || region.edgeDistance(x, z) < 0.6) continue;
    if (all.some((o) => o !== bd && x > o.x0 - 0.3 && x < o.x1 + 0.3 && z > o.z0 - 0.3 && z < o.z1 + 0.3)) continue;
    out.push({ side, w: 16, h: 26 });
  }
  if (!out.length) throw new Error(`${tag}: ${bd.name ?? 'building'} has no open side for a door`);
  return out;
}

/** Shop sign above the centred door on `side` of a voxel box: a board with letters (px 1, 5 voxels tall) from y = G + 27. */
export function shopSign(b: MapBuilder, box: { x0: number; z0: number; x1: number; z1: number }, side: Side, text: string, bg: number, fg: number): void {
  const w = signWidth(text, 1) + 4;
  const y0 = G + 27;
  const cx = (box.x0 + box.x1) >> 1;
  const cz = (box.z0 + box.z1) >> 1;
  const run = side === 'n' || side === 's' ? box.x1 - box.x0 : box.z1 - box.z0;
  if (w + 4 > run) return;
  if (side === 's') {
    b.box(cx - (w >> 1), y0, box.z1, cx - (w >> 1) + w, y0 + 7, box.z1 + 1, bg);
    signText(b, text, cx - (w >> 1) + 2, y0 + 6, box.z1 + 1, 's', fg, 1);
  } else if (side === 'n') {
    b.box(cx - (w >> 1), y0, box.z0 - 1, cx - (w >> 1) + w, y0 + 7, box.z0, bg);
    signText(b, text, cx - (w >> 1) + w - 3, y0 + 6, box.z0 - 2, 'n', fg, 1);
  } else if (side === 'e') {
    b.box(box.x1, y0, cz - (w >> 1), box.x1 + 1, y0 + 7, cz - (w >> 1) + w, bg);
    signText(b, text, box.x1 + 1, y0 + 6, cz - (w >> 1) + w - 3, 'e', fg, 1);
  } else {
    b.box(box.x0 - 1, y0, cz - (w >> 1), box.x0, y0 + 7, cz - (w >> 1) + w, bg);
    signText(b, text, box.x0 - 2, y0 + 6, cz - (w >> 1) + 2, 'w', fg, 1);
  }
}

/** Parasol with a table: pole, striped canopy and a small round table, about 2.4 m across. */
export function parasol(b: MapBuilder, cx: number, cz: number, color: number): void {
  b.cylinder(cx, cz, G, G + 9, 3, M('planks_dark_1'));
  b.box(cx - 1, G + 9, cz - 1, cx + 1, G + 11, cz + 1, M('paint_white'));
  b.box(cx, G + 11, cz, cx + 1, G + 30, cz + 1, M('paint_white'));
  b.cone(cx, G + 28, cz, 12, 6, stripes(color, M('paint_white'), 3, 'x'), 2);
}

/** Palm tree with a few coconuts (wraps MapBuilder.palm so the maps share one look). */
export function palmTree(b: MapBuilder, x: number, z: number, height = 52, dir: Side = 'e'): void {
  b.palm(x, G, z, height, dir, M('leaves_4'));
}

/** Lemon tree: a leafy tree dotted with yellow fruit. */
export function lemonTree(b: MapBuilder, x: number, z: number, height = 34): void {
  b.leafyTree(x, G, z, height, 11, M('leaves_2'));
  for (let k = 0; k < 9; k++) {
    const h = hash32(x, z, k, 77);
    const fx = x + ((h & 15) - 8);
    const fz = z + (((h >> 4) & 15) - 8);
    const fy = G + height - 3 + ((h >> 8) % 9) - 4;
    if (b.w.get(fx, fy, fz) !== AIR) b.set(fx, fy, fz, M('paint_yellow'));
  }
}


// Terrain ----------------------------------------------------------------------------------------------

/**
 * Levels natural ground over a plan rectangle (voxels) to the surface level `g` (the first air voxel, e.g. G): digs down anything that stands above it and fills
 * up anything below it with `surface`. For pads in front of stairs and doors on maps with dunes or slopes.
 */
export function flattenPad(b: MapBuilder, x0: number, z0: number, x1: number, z1: number, g: number, surface: Brush): void {
  b.clear(x0, g, z0, x1, g + 24, z1);
  for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) for (let y = g - 1; y > g - 8 && b.w.get(x, y, z) === AIR; y--) b.set(x, y, z, surface);
  b.box(x0, g - 1, z0, x1, g, z1, surface);
}

/**
 * Raises or lowers whole columns of a finished flat-ground world: column (x, z) moves by dh(x, z) voxels (buildings keep their shape wherever dh is
 * constant under them). Layers 0-1 (bedrock) stay; a raised column is filled underneath with its own deep material; a lowered column loses
 * its top. Build everything that must sit on a slope AFTER calling this, using MapBuilder.surfaceY for heights.
 */
export function warpColumns(b: MapBuilder, dh: (x: number, z: number) => number, maxY: number): void {
  const w = b.w;
  const old = new Uint8Array(b.sy);
  for (let z = 0; z < b.sz; z++)
    for (let x = 0; x < b.sx; x++) {
      const d = dh(x, z);
      if (d === 0) continue;
      const top = Math.min(b.sy, maxY + Math.max(0, d));
      for (let y = 2; y < top; y++) old[y] = w.get(x, y, z);
      for (let y = 2; y < top; y++) {
        const s = y - d;
        const v = s < 2 ? old[2] : s >= top ? AIR : old[s];
        if (w.get(x, y, z) !== v) w.set(x, y, z, v);
      }
    }
  // Staircases recorded for the stairs audit moved with their columns.
  if (STAIR_AUDIT.on) for (const r of STAIR_AUDIT.list) r.y += dh(r.x, r.z);
  if (OPENING_AUDIT.on)
    for (const r of OPENING_AUDIT.list) {
      const d = dh(r.x0, r.z0);
      r.y0 += d;
      r.y1 += d;
    }
}
